import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@sravni/react-design-system';
import { useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import {
    amountTransform,
    Col,
    onlyNumbersOutput,
    onlyRussianOutput,
    PhoneInput,
    Row,
    Rows,
    SelectInput,
    TextInput,
} from '../../../../../../../landing.common';
import {
    FORM_FIELDS,
    ORGANIZATION_CHOICE_TYPE,
    ORGANIZATION_CHOICE_TYPE_EVENT_LABELS,
    OrganizationChoiceType,
    PAGE_CONTEXT,
} from '../../../../../../../../constants';
import {
    AREA_OF_EMPLOYMENT_SOURCE,
    EMPLOYER_TYPE_SOURCES,
    EMPLOYMENT_TYPE_SOURCE,
    EMPLOYMENT_VALUES,
    JOB_TYPE_SOURCE,
    LAST_EXPERIENCE_MONTHS_SOURCE,
    LAST_EXPERIENCE_YEARS_SOURCE,
    SENIORITY_SOURCE,
    UNEMPLOYMENT_REASON_SOURCE,
    UNEMPLOYMENT_REASONS,
} from '../../../../../../../../constants/sources';
import { FormAddressSuggest } from '../../../../../../../form/ui/suggest/address';
import { FormOrganizationSuggest } from '../../../../../../../form/ui/suggest/organization';
import { JobTitleSuggest } from '../../../../../../../form/ui/suggest/job.title';
import { EventType } from '../../../../../../../../helpers/analytics';
import { api } from '../../../../../../../../helpers/client';
import { getAddressString, removeAllStreetPartsAfterHouse, sourceValueByCode } from '../../../../../../../../helpers';
import { findAreaOfEmploymentByOkved, getProperOrganizationName } from '../../../../../../../form/steps/work/helpers';
import { extendFbPayload, setPageContext } from '../../../../../../../../store/reducers/formStore';
import { useFormData, useFormStore } from '../../../../../../../../store/selectors/formStore';
import { EMPLOYMENT_TYPE_DEPS } from '../../../../../../../../constants/field.dependencies';
import { namesOfOrganizationFields } from '../../../../../../../form/steps/work/constants';
import { useFormOnBlur } from '../../../../../../../form/hooks/formHooks';
import { useAnalytics } from '../../../../../../../hooks/analytics';
import { schema } from './schema';

type Props = {
    onNext: () => void;
    onAutoValidationError: (e: ValidationError) => void;
};

/** Место работы */
export const Step3: React.FC<Props> = (props) => {
    const { onNext, onAutoValidationError } = props;
    const dispatch = useDispatch();
    const formData = useFormData();
    const { autoRun } = useFormStore();
    const { pushEvent } = useAnalytics();

    const form = useForm({ resolver: yupResolver(schema()), mode: 'onSubmit', reValidateMode: 'onBlur' });

    const {
        control,
        handleSubmit,
        /** dirtyFields are needed for keepDirtyValues */
        formState: { errors, dirtyFields: _, isValidating },
        trigger,
        getValues,
        setValue,
        reset,
        watch,
    } = form;

    const organizationChoiceType = useRef<OrganizationChoiceType | null>(null);
    const organizationStatus = useRef<string | null>(null);

    const inProcess = isValidating;

    const employmentType = watch(FORM_FIELDS.EMPLOYMENT_TYPE);
    const unemploymentReason = watch(FORM_FIELDS.UNEMPLOYMENT_REASON);
    const workFields: Array<FE.FieldName> = useMemo(
        () =>
            EMPLOYMENT_TYPE_DEPS[employmentType as ValuesOf<typeof EMPLOYMENT_VALUES>] ||
            EMPLOYMENT_TYPE_DEPS[EMPLOYMENT_VALUES.EMPLOYMENT],
        [employmentType],
    );

    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        dispatch(setPageContext(PAGE_CONTEXT.STEP_3));
        if (autoRun) {
            schema().validate(formData).then(onSubmit).catch(onAutoValidationError);
        }
    }, [autoRun]);

    const emitThirdStepAnalytics = () => {
        const [type, area, jobType] = getValues([
            FORM_FIELDS.EMPLOYMENT_TYPE,
            FORM_FIELDS.AREA_OF_EMPLOYMENT,
            FORM_FIELDS.JOB_TYPE,
        ]);
        const employmentTypeChosen = employmentTypeOptions.find((item) => item.value === type);
        const areaChosen = areaOfEmploymentOptions.find((item) => item.value === area);
        const jobChosen = jobTypeOptions.find((item) => item.value === jobType);

        if (organizationChoiceType.current) {
            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Место работы',
                eventLabel: ORGANIZATION_CHOICE_TYPE_EVENT_LABELS[organizationChoiceType.current],
            });
        }

        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Шаг 3|Место работы',
            eventLabel: `Тип занятости - ${
                employmentTypeChosen ? employmentTypeChosen.label : ''
            }|Сфера деятельности - ${areaChosen ? areaChosen.label : ''}|Тип должности - ${
                jobChosen ? jobChosen.label : ''
            }`,
        });
    };

    const onSubmit = async () => {
        dispatch(extendFbPayload({ work_organization_status: organizationStatus.current || undefined }));

        emitThirdStepAnalytics();
        onNext();
    };

    const formOnFocus: React.FocusEventHandler<HTMLFormElement> = (event) => {
        const { target } = event;
        /** Некоторые элементы, например Select, отдают div вместо конечного input */
        const inputElem =
            (target.hasAttribute('name') && target.hasAttribute('value') && target) ||
            target.querySelector('[name][value]');
        if (!inputElem) {
            return;
        }
    };

    const employmentTypeOptions = useMemo(
        () => EMPLOYMENT_TYPE_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const employerTypeOptions = useMemo(
        () => EMPLOYER_TYPE_SOURCES.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const unEmploymentReasonOptions = useMemo(
        () => UNEMPLOYMENT_REASON_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const jobTypeOptions = useMemo(() => JOB_TYPE_SOURCE.map(({ name: label, value }) => ({ label, value })), []);

    const seniorityOptions = useMemo(() => SENIORITY_SOURCE.map(({ name: label, value }) => ({ label, value })), []);

    const areaOfEmploymentOptions = useMemo(
        () => AREA_OF_EMPLOYMENT_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const monthsOptions = useMemo(
        () => LAST_EXPERIENCE_MONTHS_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const yearsOptions = useMemo(
        () => LAST_EXPERIENCE_YEARS_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const clearFields = useCallback(
        (fields: ReadonlyArray<string>) => {
            fields.forEach((fieldName) => setValue(fieldName, ''));
        },
        [setValue],
    );

    const onOrganizationNameClear = useCallback(() => {
        clearFields(namesOfOrganizationFields);
    }, [clearFields]);

    const onOrganizationNameBlur = async (
        event: FE.FieldChangeEvent,
        onBlur: (event: FE.FieldChangeEvent) => void,
        option?: Dadata.OrganizationSuggestItem,
    ) => {
        if (option) {
            if (!option.data?.phones) {
                const refinedOrganization = await api.organizationByInn(option.data.inn);
                if (refinedOrganization) {
                    option = refinedOrganization;
                }
            }

            const {
                value,
                data: {
                    opf,
                    inn,
                    address,
                    okved,
                    phones,
                    name: { full: fullOrgName, short: shortOrgName },
                },
            } = option || { data: {} };

            const areaOfEmployment = okved && findAreaOfEmploymentByOkved(okved);
            const { code: opfCode } = opf || {};
            const { value: suggestAddress, data: addressData } = address || {};

            let addressValue = suggestAddress;

            const fullName = formData[FORM_FIELDS.FULL_NAME] as string;

            if (opfCode === '50102' && value.includes(fullName)) {
                /** Для ИП, кто работает сам на себя, подставляем адрес регистрации в рабочий адрес */
                const street = formData[FORM_FIELDS.REGISTRATION_STREET] as string;
                const apartment = formData[FORM_FIELDS.REGISTRATION_APARTMENT] as string;
                addressValue = (street && getAddressString(street, apartment)) || addressValue;
            }
            const opfValue = sourceValueByCode(opfCode, EMPLOYER_TYPE_SOURCES);

            addressValue = removeAllStreetPartsAfterHouse(addressValue);

            const minOrganizationName = getProperOrganizationName([value, shortOrgName]);
            const update: Array<{ name: FE.FieldName; value: any }> = [
                { name: FORM_FIELDS.ORGANIZATION_NAME, value: minOrganizationName },
                { name: FORM_FIELDS.EMPLOYER_TYPE, value: opfValue },
                { name: FORM_FIELDS.EMPLOYER_INN, value: inn },
                { name: FORM_FIELDS.WORK_ADDRESS, value: addressValue },
            ];

            if (areaOfEmployment) {
                update.push({ name: FORM_FIELDS.AREA_OF_EMPLOYMENT, value: areaOfEmployment.value });
            }

            if (phones && phones.length) {
                const phone = phones.reduce((correctNumber: string, currentPhone: { value?: string }) => {
                    if (correctNumber) {
                        return correctNumber;
                    }
                    const currentValue = currentPhone.value || '';
                    const trying = currentValue.split(',')[0].replace(/\D/g, '');
                    if (trying.length >= 10) {
                        return trying;
                    }
                }, '');

                update.push({ name: FORM_FIELDS.WORK_PHONE, value: `+7${phone.substr(-10)}` });
            }

            update.forEach((item) => {
                setValue(item.name, item.value);
            });

            const workPhone = formData[FORM_FIELDS.WORK_PHONE] as string;
            if (workPhone) {
                await trigger(FORM_FIELDS.WORK_PHONE);
            }
            organizationStatus.current = option.data.state.status;
            organizationChoiceType.current = ORGANIZATION_CHOICE_TYPE.SUGGEST;
        } else {
            organizationChoiceType.current = ORGANIZATION_CHOICE_TYPE.BY_YOURSELF;
        }

        onBlur(event);
    };

    const isEmploymentTypeNotWorkingOrRetired =
        employmentType === EMPLOYMENT_VALUES.NOT_WORKING_VALUE || employmentType === EMPLOYMENT_VALUES.RETIRED;

    const isAnotherUnemploymentReason =
        employmentType === EMPLOYMENT_VALUES.NOT_WORKING_VALUE && unemploymentReason === UNEMPLOYMENT_REASONS.OTHER;

    const formOnBlur = useFormOnBlur(form, false);

    return (
        <form onSubmit={handleSubmit(onSubmit)} onBlur={formOnBlur} onFocus={formOnFocus}>
            <Rows>
                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.EMPLOYMENT_TYPE}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    withCrossIcon={false}
                                    options={employmentTypeOptions}
                                    label="Тип занятости"
                                    errorMessage={errors[FORM_FIELDS.EMPLOYMENT_TYPE]?.message}
                                />
                            )}
                        />
                    </Col>
                    {workFields.includes(FORM_FIELDS.UNEMPLOYMENT_REASON) && (
                        <Col md-6>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.UNEMPLOYMENT_REASON}
                                render={({ field }) => (
                                    <SelectInput
                                        {...field}
                                        options={unEmploymentReasonOptions}
                                        withCrossIcon={false}
                                        label="Причина отсутствия занятости"
                                        errorMessage={errors[FORM_FIELDS.UNEMPLOYMENT_REASON]?.message}
                                    />
                                )}
                            />
                        </Col>
                    )}
                    {workFields.includes(FORM_FIELDS.ORGANIZATION_NAME) && (
                        <Col md-6>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.ORGANIZATION_NAME}
                                render={({ field: { onBlur, ...field } }) => (
                                    <FormOrganizationSuggest
                                        input={{
                                            ...field,
                                            label: 'Место работы',
                                            errorMessage: errors[FORM_FIELDS.ORGANIZATION_NAME]?.message,
                                        }}
                                        onChange={field.onChange}
                                        onBlur={(event, option) => onOrganizationNameBlur(event, onBlur, option)}
                                        onClear={onOrganizationNameClear}
                                        maxLength={150}
                                        count={5}
                                    />
                                )}
                            />
                        </Col>
                    )}
                </Row>

                {isAnotherUnemploymentReason && (
                    <Row>
                        <Col>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        onChange={onlyRussianOutput(field)}
                                        label="Другая причина"
                                        errorMessage={errors[FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON]?.message}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                )}

                {!isEmploymentTypeNotWorkingOrRetired && (
                    <Row>
                        {workFields.includes(FORM_FIELDS.LAST_EXPERIENCE_START_MONTH) && (
                            <Col md-3 sm-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.LAST_EXPERIENCE_START_MONTH}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            withCrossIcon={false}
                                            options={monthsOptions}
                                            label="Месяц"
                                            errorMessage={errors[FORM_FIELDS.LAST_EXPERIENCE_START_MONTH]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                        {workFields.includes(FORM_FIELDS.LAST_EXPERIENCE_START_YEAR) && (
                            <Col md-3 sm-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.LAST_EXPERIENCE_START_YEAR}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            options={yearsOptions}
                                            withCrossIcon={false}
                                            label="Год"
                                            errorMessage={errors[FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                        {workFields.includes(FORM_FIELDS.WORK_PHONE) && (
                            <Col md-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.WORK_PHONE}
                                    render={({ field }) => (
                                        <PhoneInput
                                            {...field}
                                            label="Рабочий телефон"
                                            onChange={onlyNumbersOutput(field)}
                                            errorMessage={errors[FORM_FIELDS.WORK_PHONE]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                    </Row>
                )}

                {!isEmploymentTypeNotWorkingOrRetired && workFields.includes(FORM_FIELDS.WORK_ADDRESS) && (
                    <Row>
                        <Col>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.WORK_ADDRESS}
                                render={({ field: { onBlur, ...field } }) => (
                                    <FormAddressSuggest
                                        input={{
                                            ...field,
                                            label: 'Рабочий адрес',
                                            errorMessage: errors[FORM_FIELDS.WORK_ADDRESS]?.message,
                                        }}
                                        onChange={field.onChange}
                                        onBlur={onBlur}
                                        toBound="house"
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                )}

                {!isEmploymentTypeNotWorkingOrRetired && (
                    <Row>
                        {workFields.includes(FORM_FIELDS.AREA_OF_EMPLOYMENT) && (
                            <Col md-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.AREA_OF_EMPLOYMENT}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label="Сфера деятельности"
                                            withCrossIcon={false}
                                            options={areaOfEmploymentOptions}
                                            errorMessage={errors[FORM_FIELDS.AREA_OF_EMPLOYMENT]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                        {workFields.includes(FORM_FIELDS.EMPLOYER_INN) && (
                            <Col md-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.EMPLOYER_INN}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            label="ИНН организации (необязат.)"
                                            errorMessage={errors[FORM_FIELDS.EMPLOYER_INN]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                    </Row>
                )}

                <Row>
                    {workFields.includes(FORM_FIELDS.EMPLOYER_TYPE) && (
                        <Col md-6>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.EMPLOYER_TYPE}
                                render={({ field }) => (
                                    <SelectInput
                                        {...field}
                                        label="Тип организации"
                                        withCrossIcon={false}
                                        options={employerTypeOptions}
                                        errorMessage={errors[FORM_FIELDS.EMPLOYER_TYPE]?.message}
                                    />
                                )}
                            />
                        </Col>
                    )}
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.SENIORITY}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Общий трудовой стаж"
                                    withCrossIcon={false}
                                    options={seniorityOptions}
                                    errorMessage={errors[FORM_FIELDS.SENIORITY]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                {!isEmploymentTypeNotWorkingOrRetired && (
                    <Row>
                        {workFields.includes(FORM_FIELDS.JOB_TITLE) && (
                            <Col md-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.JOB_TITLE}
                                    render={({ field: { onBlur, ...field } }) => (
                                        <JobTitleSuggest
                                            input={{
                                                ...field,
                                                label: 'Название должности',
                                                errorMessage: errors[FORM_FIELDS.JOB_TITLE]?.message,
                                            }}
                                            onChange={field.onChange}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                        {workFields.includes(FORM_FIELDS.JOB_TYPE) && (
                            <Col md-6>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.JOB_TYPE}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label="Тип должности"
                                            withCrossIcon={false}
                                            options={jobTypeOptions}
                                            errorMessage={errors[FORM_FIELDS.JOB_TYPE]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        )}
                    </Row>
                )}

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.MONTHLY_SALARY}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Ежемесячный доход"
                                    value={amountTransform.input(field)}
                                    onChange={amountTransform.output(field)}
                                    errorMessage={errors[FORM_FIELDS.MONTHLY_SALARY]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-8 />
                    <Col md-4>
                        <Button loading={inProcess} block variant="primary" type="submit">
                            Продолжить
                        </Button>
                    </Col>
                </Row>
            </Rows>
        </form>
    );
};
